import React from "react";
import type { UserBooking, BookedTimeSlots } from "../store/types";
import { useTranslation } from "react-i18next";

interface BookingCardProps {
  booking: UserBooking;
  onBook: (booking: UserBooking) => void;
  onDeleteBooking: (userBookingId: number, bookingId: number) => void;
}

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  onBook,
  onDeleteBooking,
}) => {
  const { t, i18n } = useTranslation();

  const resolvedLocale =
    i18n?.language || (typeof navigator !== "undefined" ? navigator.language : "en");

  const dateFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(resolvedLocale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [resolvedLocale]
  );

  const timeFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(resolvedLocale, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    [resolvedLocale]
  );

  const handleDelete = async (bookingTimeSlotId: number) => {
    const confirmed = window.confirm(t("bookingCard.confirmDelete"));
    if (!confirmed) return;
    onDeleteBooking(booking.id, bookingTimeSlotId);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      {booking.propertyOwner.logo && (
        <div
          className="h-40 w-full bg-cover bg-center"
          style={{ backgroundImage: `url(${booking.propertyOwner.logo})` }}
        />
      )}
      <div className="flex flex-1 flex-col p-5">
        <h3 className="mb-2 text-xl font-bold text-gray-900">{booking.name}</h3>
        {booking.location && (
          <p className="mb-2 flex items-center text-sm text-gray-600">
            <svg
              className="mr-1.5 h-4 w-4 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
            {booking.location.locationAddress}
            {booking.location.city ? `, ${booking.location.city}` : ""}
          </p>
        )}
        <p className="mb-4 line-clamp-2 text-sm text-gray-600">
          {booking.description}
        </p>

        {/* Bookings List Section */}
        {booking.bookedTimeSlots && booking.bookedTimeSlots.length > 0 && (
          <div className="mb-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-700">
                {t("bookingCard.yourBookings")}
              </h4>
              <span className="rounded-full bg-indigo-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                {booking.bookedTimeSlots.length}
              </span>
            </div>
            <div className="h-36 space-y-2 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-indigo-300 scrollbar-track-transparent">
              {booking.bookedTimeSlots.map((bookingItem: BookedTimeSlots) => {
                const startDate = new Date(bookingItem.startDate);
                const endDate = new Date(bookingItem.endDate);

                const formattedDate = dateFormatter.format(startDate);
                const formattedStartTime = timeFormatter.format(startDate);
                const formattedEndTime = timeFormatter.format(endDate);

                return (
                  <div
                    key={bookingItem.id}
                    className="group flex items-center justify-between rounded-md bg-white p-3 shadow-sm transition-all duration-200 hover:shadow-md"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full">
                        <svg
                          className="h-4 w-4 text-indigo-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{formattedDate}</p>
                        <p className="text-xs font-semibold text-gray-600">{`${formattedStartTime} - ${formattedEndTime}`}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(bookingItem.id)}
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-red-600 transition-all duration-200 hover:bg-red-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={t("bookingCard.deleteAria")}
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Book Button */}
        <div className="mt-auto">
          <button
            onClick={() => onBook(booking)}
            className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-primary-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <span className="flex items-center justify-center">
              <svg
                className="mr-2 h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t("bookingCard.bookNewSlot")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingCard;
